import React, { FC } from 'react'
import classNamesBind from 'classnames/bind'
import css from './styles.module.scss'
import ProfileImage from '@dashboard/components/ProfileImage'
import Button from '@dashboard/components/Button'
import ColorIcon from '@dashboard/components/ColorIcon'
import Loader from '@crystal-eyes/components/elements/Loader/Loader'
import type { ColorIconName } from '@dashboard/lib/types'

interface OwnProps {
  teamName: string
  licenseInfo: {
    sales: boolean
    leadership: boolean
    hiring: boolean
  }
  inviterInfo?: {
    name: string
    photoURL: string | null
  }
  loading: boolean
  accept: () => void
  decline: () => void
}

const classNames = classNamesBind.bind(css)

const TeamInviteForm: FC<OwnProps> = ({
  teamName,
  licenseInfo,
  inviterInfo,
  loading,
  accept,
  decline,
}: OwnProps) => {
  // Partials
  const renderProfileImage = () => {
    if (!inviterInfo) return null

    return <ProfileImage size={50} photoUrl={inviterInfo.photoURL} />
  }

  const renderInviterInfo = () => {
    if (!inviterInfo) return null

    return (
      <div className={classNames('inviterInfo')}>
        by <b>{inviterInfo.name}</b>
      </div>
    )
  }

  const renderLicenseWithIcon = (icon: ColorIconName, title: string) => {
    return (
      <div key={title} className={classNames('licenseRow')}>
        <ColorIcon icon={icon} width={12} discType="blue" />
        <div className={classNames('licenseText')}>{title}</div>
      </div>
    )
  }

  const renderLicense = (license: string) => {
    switch (license) {
      case 'sales':
        return renderLicenseWithIcon('tag', 'Sales')
      case 'leadership':
        return renderLicenseWithIcon('profiles', 'Teams')
      case 'hiring':
        return renderLicenseWithIcon('magnifying-glass-person-small', 'Hiring')
      default:
        return null
    }
  }

  const renderLicenseInfo = () => {
    const licenses = Object.keys(licenseInfo).filter(
      key => (licenseInfo as Record<string, boolean>)[key],
    )

    if (licenses.length === 0) return null

    return (
      <div className={classNames('licenseInfo')}>
        <div className={classNames('joinText')}>
          Joining this organization will give you access to the following tools:
        </div>
        <div className={classNames('licensesRow')}>
          {licenses.map(renderLicense)}
        </div>
      </div>
    )
  }

  const renderActions = () => {
    if (loading) {
      return (
        <div className={classNames('actions')}>
          <Loader blue size={32} />
        </div>
      )
    }

    return (
      <div className={classNames('actions')}>
        <div onClick={decline} className={classNames('decline')}>
          <ColorIcon icon="close" width={8} discType="red" />
          <div className={classNames('declineText')}>Decline Invite</div>
        </div>
        <Button small onClick={accept} noMargin actionBlue>
          Accept Invite
        </Button>
      </div>
    )
  }

  return (
    <div className={classNames('container')}>
      <div className={classNames('content')}>
        <div className={classNames('inviteDetails')}>
          {renderProfileImage()}
          <div className={classNames('inviteText')}>
            You've been invited to join <b>{teamName}</b> {renderInviterInfo()}
          </div>
        </div>
        {renderLicenseInfo()}
      </div>
      {renderActions()}
    </div>
  )
}

export default TeamInviteForm
